import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import './Pricing.css';

const Pricing = () => {
  return (
    <section id="pricing" className="pricing section">
      <div className="container">
        <motion.div 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Our All-Inclusive Website Plans</h2>
          <p>Transparent pricing for professionally managed websites. Let us handle the tech, so you can focus on your business.</p>
        </motion.div>
        
        <div className="pricing-container">
          {/* Essential Plan */}
          <motion.div 
            className="pricing-card starter"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="pricing-badge">STARTER</div>
            <h3 className="pricing-title">Essential</h3>
            <p className="pricing-description">
              Ideal for startups and small businesses needing a professional, hassle-free online presence.
            </p>
            <div className="pricing-price">
              <span className="price">£39</span>/month
              <div className="setup-fee">+ £169 one-time setup</div>
              <div className="term">No minimum contract</div>
            </div>
            <ul className="pricing-features">
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Professionally Designed Website</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Up to 10 Pages</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Fully Responsive (Mobile & Tablet)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Easy-to-Use Content Editor</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Unlimited Content Updates</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Foundational SEO Setup</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Secure Contact Form</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Social Media Links</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Google Analytics Integration</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Connect Your Existing Domain</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> 5 Professional Email Accounts (5GB each)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Secure & Fast Web Hosting</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> SSL Security Certificate</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Ongoing Maintenance & Security Updates</li>
              <li><FontAwesomeIcon icon={faTimes} className="icon-times" /> E-commerce Store</li>
            </ul>
            <a href="#contact" className="pricing-button">Choose Essential</a>
            <p className="pricing-footer">
              Setup fee invoiced separately after initial consultation.
            </p>
          </motion.div>
          
          {/* Pro Plan */}
          <motion.div 
            className="pricing-card popular"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="pricing-badge business">BUSINESS CHOICE</div>
            <h3 className="pricing-title">Pro</h3>
            <p className="pricing-description">
              For growing businesses requiring e-commerce, more pages, and enhanced features.
            </p>
            <div className="pricing-price">
              <span className="price">£79</span>/month
              <div className="setup-fee">+ £399 one-time setup</div>
              <div className="term">No minimum contract</div>
            </div>
            <ul className="pricing-features">
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> All Features in Essential</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Up to 30 Pages</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Free Domain Name (.co.uk or .com)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Enhanced Design Customisation</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> E-commerce Ready Store</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Up to 50 Products</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Secure Payment Gateway Setup</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Advanced Contact & Booking Forms</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Integrated Blog Platform</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Advanced SEO Toolkit & Strategy</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Website Performance Optimization</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Access to Stock Photo Library (20 images)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> 10 Professional Email Accounts (5GB each)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Monthly Analytics & Reporting</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Priority Email & Ticket Support</li>
            </ul>
            <a href="#contact" className="pricing-button secondary-button">Choose Pro</a>
            <p className="pricing-footer">
              Setup fee invoiced separately after initial consultation.
            </p>
          </motion.div>
          
          {/* Bespoke Plan */}
          <motion.div 
            className="pricing-card custom"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="pricing-badge tertiary">ENTERPRISE SOLUTION</div>
            <h3 className="pricing-title">Bespoke</h3>
            <p className="pricing-description">
              Tailored solutions for established businesses with unique, complex, or large-scale requirements.
            </p>
            <div className="pricing-price">
              <span>from </span><span className="price">£99</span>/month
              <div className="setup-fee">+ Custom Setup Fee</div>
              <div className="term">Tailored Agreement</div>
            </div>
            <ul className="pricing-features">
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> All Features in Pro</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Unlimited Pages & Content</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Fully Bespoke Design & Development</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Advanced E-commerce Solutions</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Unlimited Products & Categories</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Custom API & Third-Party Integrations</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Custom User Logins & Member Portals</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Enhanced Security & Compliance</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Custom Database Solutions</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Productivity Suite Integration (Google/Microsoft)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> ERP/CRM & Business System Integration</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> In-depth Custom Reporting & Analytics</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Specialised Integrations (e.g., Fundraising)</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Dedicated Account Manager & Strategy Calls</li>
              <li><FontAwesomeIcon icon={faCheck} className="icon-check" /> Premium Support with SLA Guarantee</li>
            </ul>
            <a href="#contact" className="pricing-button tertiary-button">Discuss Your Project</a>
            <p className="pricing-footer">
              Let's discuss your unique needs for a custom proposal.
            </p>
          </motion.div>
        </div>
        
        <div className="pricing-notice">
          <p><strong>All plans are fully managed:</strong> We handle all technical aspects including hosting, security, maintenance, and unlimited content updates, so you can focus on running your business.</p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;